import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";

import PublicLayout from "@/components/PublicLayout";
import Home from "@/pages/Home";
import Individuals from "@/pages/Individuals";
import Business from "@/pages/Business";
import Services from "@/pages/Services";
import ServiceDetail from "@/pages/ServiceDetail";
import About from "@/pages/About";
import Testimonials from "@/pages/Testimonials";
import Insights from "@/pages/Insights";
import ArticleDetail from "@/pages/ArticleDetail";
import BookConsultation from "@/pages/BookConsultation";
import Contact from "@/pages/Contact";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import SignDocument from "@/pages/SignDocument";

import AdminLogin from "@/admin/AdminLogin";
import ForgotPassword from "@/admin/ForgotPassword";
import ResetPassword from "@/admin/ResetPassword";
import ProtectedRoute from "@/admin/ProtectedRoute";
import RequirePermission from "@/admin/RequirePermission";
import AdminLayout from "@/admin/AdminLayout";
import Dashboard from "@/admin/Dashboard";
import ContentManager from "@/admin/ContentManager";
import EnquiriesManager from "@/admin/EnquiriesManager";
import SettingsManager from "@/admin/SettingsManager";
import UsersManager from "@/admin/UsersManager";
import AuditLog from "@/admin/AuditLog";
import ConsultationsManager from "@/admin/ConsultationsManager";
import ClientsManager from "@/admin/ClientsManager";
import MattersManager from "@/admin/MattersManager";
import TasksManager from "@/admin/TasksManager";
import CalendarManager from "@/admin/CalendarManager";
import TemplatesManager from "@/admin/TemplatesManager";
import FinanceManager from "@/admin/FinanceManager";
import ComplianceManager from "@/admin/ComplianceManager";
import PoliciesManager from "@/admin/PoliciesManager";
import ComplaintsManager from "@/admin/ComplaintsManager";
import ReportsManager from "@/admin/ReportsManager";
import SignaturesManager from "@/admin/SignaturesManager";
import RemindersManager from "@/admin/RemindersManager";
import TimeManager from "@/admin/TimeManager";
import DataManager from "@/admin/DataManager";
import SecurityManager from "@/admin/SecurityManager";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route element={<PublicLayout />}>
            <Route path="/" element={<Home />} />
            <Route path="/individuals" element={<Individuals />} />
            <Route path="/business" element={<Business />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/about" element={<About />} />
            <Route path="/testimonials" element={<Testimonials />} />
            <Route path="/insights" element={<Insights />} />
            <Route path="/insights/:slug" element={<ArticleDetail />} />
            <Route path="/book-consultation" element={<BookConsultation />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/legal/:page" element={<Legal />} />
            <Route path="*" element={<NotFound />} />
          </Route>

          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/sign/:token" element={<SignDocument />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }
          >
            <Route index element={<Dashboard />} />
            <Route path="articles" element={<RequirePermission module="cms" action="view"><ContentManager type="articles" /></RequirePermission>} />
            <Route path="testimonials" element={<RequirePermission module="cms" action="view"><ContentManager type="testimonials" /></RequirePermission>} />
            <Route path="services" element={<RequirePermission module="cms" action="view"><ContentManager type="services" /></RequirePermission>} />
            <Route path="faqs" element={<RequirePermission module="cms" action="view"><ContentManager type="faqs" /></RequirePermission>} />
            <Route path="team" element={<RequirePermission module="cms" action="view"><ContentManager type="team" /></RequirePermission>} />
            <Route path="enquiries" element={<RequirePermission module="enquiries" action="view"><EnquiriesManager /></RequirePermission>} />
            <Route path="consultations" element={<RequirePermission module="consultations" action="view"><ConsultationsManager /></RequirePermission>} />
            <Route path="clients" element={<RequirePermission module="clients" action="view"><ClientsManager /></RequirePermission>} />
            <Route path="matters" element={<RequirePermission module="matters" action="view"><MattersManager /></RequirePermission>} />
            <Route path="tasks" element={<RequirePermission module="tasks" action="view"><TasksManager /></RequirePermission>} />
            <Route path="calendar" element={<RequirePermission module="calendar" action="view"><CalendarManager /></RequirePermission>} />
            <Route path="templates" element={<RequirePermission module="templates" action="view"><TemplatesManager /></RequirePermission>} />
            <Route path="signatures" element={<RequirePermission module="signatures" action="view"><SignaturesManager /></RequirePermission>} />
            <Route path="reminders" element={<RequirePermission module="reminders" action="view"><RemindersManager /></RequirePermission>} />
            <Route path="time" element={<RequirePermission module="time" action="view"><TimeManager /></RequirePermission>} />
            <Route path="finance" element={<RequirePermission module="finance" action="view"><FinanceManager /></RequirePermission>} />
            <Route path="compliance" element={<RequirePermission module="compliance" action="view"><ComplianceManager /></RequirePermission>} />
            <Route path="policies" element={<RequirePermission module="compliance" action="view"><PoliciesManager /></RequirePermission>} />
            <Route path="complaints" element={<RequirePermission module="complaints" action="view"><ComplaintsManager /></RequirePermission>} />
            <Route path="reports" element={<RequirePermission module="reports" action="view"><ReportsManager /></RequirePermission>} />
            <Route path="data" element={<RequirePermission module="dataio" action="view"><DataManager /></RequirePermission>} />
            <Route path="security" element={<SecurityManager />} />
            <Route path="users" element={<RequirePermission flag="user_administration"><UsersManager /></RequirePermission>} />
            <Route path="audit" element={<RequirePermission module="audit" action="view"><AuditLog /></RequirePermission>} />
            <Route path="settings" element={<RequirePermission module="settings" action="view"><SettingsManager /></RequirePermission>} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
